/**
 * @file features/scheduleReminders/DesktopScheduleBridge.tsx
 * @brief Desktop app only: keeps scheduled-meeting / session alarms and Mac
 *        calendar events in step with the server (desktopScheduleSync.ts).
 *
 * @details
 * Syncs when the user changes (sign-out clears the alarms), when the window
 * gains focus, every 15 minutes (the app may sit in the menu bar for hours
 * while a meeting is moved on the phone), and on requestScheduleSync().
 * Renders nothing; mounted once in App next to DesktopLauncherBridge, and
 * skipped in the desktop app's own small windows and in a browser tab.
 *
 * @author DES
 * @date 09-10-2026
 */

import { useEffect } from "react";
import { useAuth } from "@/shared/auth/auth-context";
import { isCompanyAdminRole } from "@/shared/auth/homePath";
import { desktopShell } from "@/shared/desktop/desktopShell";
import { syncDesktopSchedule } from "./desktopScheduleSync";
import { onScheduleSyncRequest } from "./syncSignal";

const EVERY_MS = 15 * 60 * 1000;

export function DesktopScheduleBridge() {
  const { user, status } = useAuth();
  const isWidget = window.location.pathname.startsWith("/desktop/");
  const supported = !!desktopShell()?.setAlarms && !isWidget;

  // Superadmin has no meetings or sessions of their own here.
  const signedIn = status === "authenticated" && !!user && user.role !== "superadmin";
  const uid = signedIn && user ? String(user.id) : null;
  const base = user && isCompanyAdminRole(user.role) ? "/company" : "/app";
  const meetingPath = `${base}/meetings/scheduled`;
  const sessionPath = base === "/company" ? "/company/home" : "/app";
  // Not decided yet: wait, so a page load does not clear the alarms first.
  const settled = status === "authenticated" || status === "unauthenticated";

  useEffect(() => {
    if (!supported || !settled) return;
    const paths = { meeting: meetingPath, session: sessionPath };
    const run = () => void syncDesktopSchedule(uid, paths);
    run();
    if (!uid) return;
    const timer = window.setInterval(run, EVERY_MS);
    window.addEventListener("focus", run);
    const off = onScheduleSyncRequest(run);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", run);
      off();
    };
  }, [supported, settled, uid, meetingPath, sessionPath]);

  return null;
}
